// src/components/PlaceCard.js
import React, { useState } from 'react';
import { showDeleteConfirm, showSuccess, showError } from '../services/alertService';
import './PlaceCard.css';

const PlaceCard = ({ place, editMode, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({
    name: place.name || '',
    note: place.note || '',
    rating: place.rating || 0,
    start_time: place.start_time || '',
    end_time: place.end_time || '',
    expense: {
      food: place.expense?.food || 0,
      accommodation: place.expense?.accommodation || 0,
      transport: place.expense?.transport || 0,
      entertainment: place.expense?.entertainment || 0,
      other: place.expense?.other || 0
    }
  });

  const formatTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    return new Date(dateTimeString).toLocaleTimeString('zh-TW', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('expense.')) {
      const expenseType = name.split('.')[1];
      setEditData(prev => ({
        ...prev,
        expense: {
          ...prev.expense,
          [expenseType]: parseFloat(value) || 0
        }
      }));
    } else {
      setEditData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSave = async () => {
    if (!editData.name.trim()) {
      await showError(
        '景點名稱不能為空',
        '請輸入景點名稱。'
      );
      return;
    }

    try {
      setSaving(true);
      await onUpdate(place.id, editData);
      setIsEditing(false);
      await showSuccess(
        '景點更新成功！',
        `「${editData.name}」的資訊已成功更新。`
      );
    } catch (error) {
      console.error('Error updating place:', error);
      await showError(
        '更新失敗',
        '更新景點資訊時發生錯誤，請稍後再試。'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditData({
      name: place.name || '',
      note: place.note || '',
      rating: place.rating || 0,
      start_time: place.start_time || '',
      end_time: place.end_time || '',
      expense: {
        food: place.expense?.food || 0,
        accommodation: place.expense?.accommodation || 0,
        transport: place.expense?.transport || 0,
        entertainment: place.expense?.entertainment || 0,
        other: place.expense?.other || 0
      }
    });
    setIsEditing(false);
  };

  const handleDelete = async () => {
    const result = await showDeleteConfirm(
      '刪除景點',
      `確定要刪除「${place.name}」嗎？\n\n此操作無法復原。`
    );

    if (result) {
      try {
        await onDelete(place.id);
        await showSuccess(
          '景點已刪除',
          `「${place.name}」已成功從行程中移除。`
        );
      } catch (error) {
        console.error('Error deleting place:', error);
        await showError(
          '刪除失敗',
          '刪除景點時發生錯誤，請稍後再試。'
        );
      }
    }
  };

  const getTotalExpense = (expense) => {
    if (!expense) return 0;
    return Object.values(expense).reduce((sum, cost) => sum + (cost || 0), 0);
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span 
          key={i} 
          className={`star ${i <= rating ? 'filled' : ''}`}
        >
          ★
        </span>
      );
    }
    return stars;
  };

  if (isEditing) {
    return (
      <div className="place-card editing">
        <div className="place-edit-form">
          <div className="form-group">
            <label>景點名稱</label>
            <input
              type="text"
              name="name"
              value={editData.name}
              onChange={handleInputChange}
              placeholder="景點名稱"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>開始時間</label>
              <input
                type="datetime-local"
                name="start_time"
                value={editData.start_time}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>結束時間</label>
              <input
                type="datetime-local"
                name="end_time"
                value={editData.end_time}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label>評分</label>
            <select
              name="rating"
              value={editData.rating}
              onChange={handleInputChange}
            >
              <option value="0">未評分</option>
              <option value="1">1星</option>
              <option value="2">2星</option>
              <option value="3">3星</option>
              <option value="4">4星</option>
              <option value="5">5星</option>
            </select>
          </div>

          <div className="form-group">
            <label>心得筆記</label>
            <textarea
              name="note"
              value={editData.note}
              onChange={handleInputChange}
              placeholder="旅行心得或注意事項..."
              rows="3"
            />
          </div>

          <div className="expense-section">
            <label>費用分類</label>
            <div className="expense-grid">
              <div className="expense-item">
                <label>餐飲</label>
                <input
                  type="number"
                  name="expense.food"
                  value={editData.expense.food}
                  onChange={handleInputChange}
                  min="0"
                />
              </div>
              <div className="expense-item">
                <label>住宿</label>
                <input
                  type="number"
                  name="expense.accommodation"
                  value={editData.expense.accommodation}
                  onChange={handleInputChange}
                  min="0"
                />
              </div>
              <div className="expense-item">
                <label>交通</label>
                <input
                  type="number"
                  name="expense.transport"
                  value={editData.expense.transport}
                  onChange={handleInputChange}
                  min="0"
                />
              </div>
              <div className="expense-item">
                <label>娛樂</label>
                <input
                  type="number"
                  name="expense.entertainment"
                  value={editData.expense.entertainment}
                  onChange={handleInputChange}
                  min="0"
                />
              </div>
              <div className="expense-item">
                <label>其他</label>
                <input
                  type="number"
                  name="expense.other"
                  value={editData.expense.other}
                  onChange={handleInputChange}
                  min="0"
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button 
              onClick={handleCancel} 
              className="cancel-btn"
              disabled={saving}
            >
              取消
            </button>
            <button 
              onClick={handleSave} 
              className="save-btn"
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="alert-loading"></span>
                  保存中...
                </>
              ) : (
                '保存'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="place-card">
      <div className="place-header">
        <div className="place-info">
          <h4 className="place-name">{place.name}</h4>
          <div className="place-time">
            {formatTime(place.start_time)} - {formatTime(place.end_time)}
          </div>
        </div>
        
        {editMode && (
          <div className="place-actions">
            <button 
              onClick={() => setIsEditing(true)}
              className="edit-place-btn"
              title="編輯景點"
            >
              編輯
            </button>
            <button 
              onClick={handleDelete}
              className="delete-place-btn"
              title="刪除景點"
            >
              刪除
            </button>
          </div>
        )}
      </div>

      {place.rating > 0 && (
        <div className="place-rating">
          <div className="stars">
            {renderStars(place.rating)}
          </div>
          <span className="rating-text">({place.rating}/5)</span>
        </div>
      )}

      {place.note && (
        <div className="place-note">
          <p>{place.note}</p>
        </div>
      )}

      {getTotalExpense(place.expense) > 0 && (
        <div className="place-expense">
          <div className="expense-total">
            總花費: NT$ {getTotalExpense(place.expense).toLocaleString()}
          </div>
          <div className="expense-breakdown">
            {place.expense.food > 0 && <span>餐飲: NT$ {place.expense.food}</span>}
            {place.expense.accommodation > 0 && <span>住宿: NT$ {place.expense.accommodation}</span>}
            {place.expense.transport > 0 && <span>交通: NT$ {place.expense.transport}</span>}
            {place.expense.entertainment > 0 && <span>娛樂: NT$ {place.expense.entertainment}</span>}
            {place.expense.other > 0 && <span>其他: NT$ {place.expense.other}</span>}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaceCard;